import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook — no react-helmet dependency.
 * Sets <title>, <meta name="description">, <link rel="canonical">, og:url,
 * og:title, og:description directly on document.head for the current route.
 *
 * Usage (in any public page component):
 *   useSEO({
 *     title: "Privacy Policy — Maqam",
 *     description: "How Maqam handles your data…",
 *     path: "/privacy-policy",
 *   });
 *
 * Canonical is always computed against the production origin so Google
 * collapses preview / staging / www variants onto the one authoritative URL.
 */
const CANONICAL_ORIGIN = "https://maqam-ksa.com";

function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSEO({ title, description, path }) {
  useEffect(() => {
    const canonical = path
      ? `${CANONICAL_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`
      : CANONICAL_ORIGIN + "/";

    if (title) document.title = title;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    setLink("canonical", canonical);
    setMeta("property", "og:url", canonical);
  }, [title, description, path]);
}

export default useSEO;
